import styled from '@emotion/styled';
import { type ComponentRef, useEffect, useRef, useState } from 'react';
import { Menu } from '~/modules/Elements/AKUI/Menu';
import { MenuButton } from '~/modules/Elements/Menu';
import Modal from '~/modules/Elements/Modal';
import GameState from '~/modules/GameEngine/GameState/GameState';
import SongsService from '~/modules/Songs/SongsService';
import useKeyboardNav from '~/modules/hooks/useKeyboardNav';
import useSmoothNavigate from '~/modules/hooks/useSmoothNavigate';
import RateSong from '~/routes/Game/Singing/GameOverlay/Components/RateSong';
import SelectInputModal from '~/routes/SelectInput/SelectInputModal';
import InputLag from '~/routes/Settings/InputLag';

interface Props {
  onResume: () => void;
  onExit?: () => void;
  onRestart: () => void;
  open: boolean;
}

const PauseMenuContent = ({ onResume, onExit, onRestart }: Omit<Props, 'open'>) => {
  const navigate = useSmoothNavigate();
  const menuRef = useRef<null | HTMLButtonElement>(null);
  const inputLagRef = useRef<ComponentRef<typeof InputLag>>(null);

  useEffect(() => {
    menuRef.current?.focus();
  }, [menuRef]);

  const [isInputModalOpen, setIsInputModalOpen] = useState(false);

  const { register } = useKeyboardNav({ enabled: !isInputModalOpen });

  const [rateSongOpen, setRateSongOpen] = useState(false);
  // Feature pending: the song editor is not ready for the MVP user flow yet.
  const showEditSongAction = false;
  const handleExit = async () => {
    const progress = GameState.getSongCompletionProgress();
    const songPreview = (await SongsService.getIndex()).find((song) => song.id === GameState.getSong()?.id);

    if (!songPreview?.local && progress < 0.7) {
      setRateSongOpen(true);
    } else {
      onExit?.();
    }
  };

  const onInputLagActive = () => inputLagRef.current?.element?.focus();

  return (
    <>
      {!rateSongOpen && (
        <>
          <PausePanel>
            <Menu spacing="tight">
              <MenuButton {...register('button-resume-song', onResume)} ref={menuRef}>
                Resume song
              </MenuButton>
              <MenuButton {...register('button-restart-song', onRestart)}>Restart song</MenuButton>
              <MenuButton {...register('button-exit-song', handleExit)}>Exit song</MenuButton>
              <MenuButton {...register('input-settings', () => setIsInputModalOpen(true))}>
                Microphones settings
              </MenuButton>
              <hr />
              <InputLag ref={inputLagRef} {...register('input-lag', onInputLagActive)} />
              {showEditSongAction && (
                <MenuButton
                  {...register('edit-song', () => navigate(`edit/song/`, { song: GameState.getSong()?.id ?? '' }))}
                  size="small">
                  Edit song
                </MenuButton>
              )}
            </Menu>
          </PausePanel>

          <SelectInputModal
            onClose={() => setIsInputModalOpen(false)}
            closeButtonText={'Back to Pause Menu'}
            open={isInputModalOpen}
          />
        </>
      )}
      {rateSongOpen && <RateSong onExit={onExit} register={register} song={GameState.getSong()} />}
    </>
  );
};

const PausePanel = styled.div`
  width: min(92vw, 390px);
  padding: 14px;
  border: 1px solid var(--walky-border);
  border-top: 3px solid var(--walky-orange);
  border-radius: 8px;
  background: var(--walky-surface-translucent);
  color: var(--walky-ink);
  box-shadow: var(--walky-shadow-strong);
  backdrop-filter: blur(18px);

  hr {
    width: 100%;
    margin: 0.25rem 0;
    border: 0;
    border-top: 1px solid var(--walky-border);
  }

  @media (max-width: 520px) {
    width: min(100%, 360px);
    padding: 12px;
  }
`;

export default function PauseMenu({ onResume, onExit, onRestart, open }: Props) {
  return (
    <Modal onClose={onResume} open={open}>
      {open && <PauseMenuContent onResume={onResume} onExit={onExit} onRestart={onRestart} />}
    </Modal>
  );
}
